/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 *
 *
 */

// The base that we're using when it comes to the editor. In this case: ClassicEditor.
/* eslint max-classes-per-file: ["error", 2] */
/* eslint-disable import/no-extraneous-dependencies */

// Plugins that we're using in the editor, mainly features that are being utilized.
// Essential imports for every build of CKEditor, these are required.

import {
  Alignment,
  Autoformat,
  Autosave,
  BlockQuote,
  BlockToolbar,
  Bold,
  Emoji,
  Essentials,
  Font,
  Heading,
  Highlight,
  HorizontalLine,
  Indent,
  IndentBlock,
  Italic,
  Link,
  List,
  ListProperties,
  Mention,
  Paragraph,
  PasteFromOffice,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableProperties,
  TableColumnResize,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  WordCount,
  type EditorConfig, FontColorConfig, HeadingConfig, LinkConfig, HighlightConfig, EmojiConfig,
} from 'ckeditor5';

/* eslint-enable import/no-extraneous-dependencies */

import BulbBlockId from './plugins/bulb-block-id/bulbblockid';

// Plugins that we've written ourselves.
import BulbAttachedFile from './plugins/bulb-attached-file/bulbattachedfile';
import BulbAudio from './plugins/bulb-audio/bulbaudio';
import BulbBlockAlignment from './plugins/bulb-block-alignment/bulbblockalignment';
// eslint-disable-next-line import/no-cycle
import BulbCaption from './plugins/bulb-caption/bulbcaption';
import BulbCancelUpload from './plugins/bulb-progress/bulb-upload/bulbcancelupload';
import BulbCarouselTransition from './plugins/bulb-image-carousel/carousel-transition/bulbcarouseltransition';
import BulbCropImage from './plugins/bulb-image/bulb-imagecrop/bulbimagecrop';
import BulbComment from './plugins/bulb-comment/bulbcomment';
import BulbDeleteBlock from './plugins/bulb-delete-block/bulbdeleteblock';
import BulbHeading from './plugins/bulb-heading/bulbheading';
import BulbImage from './plugins/bulb-image/bulbimage';
import BulbImageCarousel from './plugins/bulb-image-carousel/bulbimagecarousel';
import BulbList from './plugins/bulb-list/bulblist';
import BulbMedia from './plugins/bulb-media/bulbmedia';
import BulbMediaEmbed from './plugins/bulb-media-embed/bulbmediaembed';
import BulbResize from './plugins/bulb-resize/bulbresize';
import BulbRetryFailedUpload from './plugins/bulb-progress/bulb-upload/bulbretryfailedupload';
import BulbRotateImage from './plugins/bulb-image/bulb-imagerotate/bulbimagerotate';
import BulbTranscoding from './plugins/bulb-progress/bulb-transcoding/bulbtranscoding';
import BulbUpload from './plugins/bulb-progress/bulb-upload/bulbupload';
import BulbVideo from './plugins/bulb-video/bulbvideo';

// Exports from the main file that we can maintain.
export const blocksWithCaptions = ['image', 'video', 'audio', 'embed'];
export const blocksWithAlignment = ['image', 'video', 'audio', 'embed'];

const EXTERNAL_LINKS_REGEXP = /^(https?:)?\/\//;
// Plugins to include in the build.
const EDITOR_PLUGINS: any = [
  Alignment, Autoformat, Autosave, BlockQuote, BlockToolbar, Bold, Essentials, Emoji, Font, Heading, Highlight, HorizontalLine, Indent,
  IndentBlock, Italic, Link, List, ListProperties, Paragraph, PasteFromOffice, Subscript, Superscript, Table, TableCellProperties,
  TableProperties, TableColumnResize, TableToolbar, TextTransformation, TodoList, Underline, WordCount,
  BulbAttachedFile, BulbAudio, BulbBlockAlignment, BulbCaption, BulbCancelUpload, BulbCropImage, BulbComment,
  BulbCarouselTransition, BulbDeleteBlock, BulbHeading, BulbImage, BulbImageCarousel, BulbList, BulbMedia,
  BulbMediaEmbed, BulbResize, BulbRetryFailedUpload, BulbRotateImage, BulbTranscoding, BulbUpload, BulbVideo, Mention,
  TableCaption, BulbBlockId,
];

// TODO CG: find way to use   removeColor: false,
const fontColor: FontColorConfig = {
  colors: [
    { color: '#28292d', label: 'Black' },
    { color: '#96223B', label: 'Dark red' },
    { color: '#634806', label: 'Brown' },
    { color: '#038162', label: 'Dark green' },
    { color: '#7A239E', label: 'Dark purple' },
    { color: '#4D4F52', label: 'Dark gray' },
    { color: '#C42D4E', label: 'Red' },
    { color: '#AA8F00', label: 'Dark yellow' },
    { color: '#21219A', label: 'Dark blue' },
    { color: '#AB47BC', label: 'Purple' },
    { color: '#6B6E70', label: 'Gray' },
    { color: '#EF6C00', label: 'Orange' },
    { color: '#25A952', label: 'Green' },
    { color: '#2574A9', label: 'Blue' },
    { color: '#F06292', label: 'Pink' },
  ],
  columns: 5,
  documentColors: 0,
  colorPicker: false,
};

const fontFamily = {
  options: [
    'calluna, serif',
    'baskerville-urw, serif',
    'courier-prime, monospace',
    'museo-sans, sans-serif',
    'proxima-nova, sans-serif',
  ],
};

const heading: HeadingConfig = {
  options: [
    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
    {
      model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2',
    },
    {
      model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3',
    },
  ],
};

const link: LinkConfig = {
  addTargetToExternalLinks: false,
  defaultProtocol: 'https://',
  decorators: {
    // Rule for links containing 'bulbapp.com'
    bulbAppLinks: {
      mode: 'automatic',
      callback: (url: any) =>
        // Ensure the URL is valid and contain 'bulbapp.com'
        !!url && EXTERNAL_LINKS_REGEXP.test(url) && url.includes('bulbapp.com'),
      attributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
    // Rule for links NOT containing 'bulbapp.com'
    externalLinks: {
      mode: 'automatic',
      callback: (url: any) =>
        // Ensure the URL is valid and does not contain 'bulbapp.com'
        !!url
          && EXTERNAL_LINKS_REGEXP.test(url)
          && !url.includes('bulbapp.com'),
      attributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow ugc',
      },
    },
  },
};

const highlight: HighlightConfig = {
  options: [
    {
      model: 'yellowMarker',
      class: 'marker-yellow',
      title: 'Yellow Marker',
      color: 'var(--ck-content-highlight-marker-yellow)',
      type: 'marker',
    },
    {
      model: 'greenMarker',
      class: 'marker-green',
      title: 'Green marker',
      color: 'var(--ck-content-highlight-marker-green)',
      type: 'marker',
    },
    {
      model: 'blueMarker',
      class: 'marker-blue',
      title: 'Blue marker',
      color: 'var(--ck-content-highlight-marker-blue)',
      type: 'marker',
    },
    {
      model: 'redPen',
      class: 'pen-red',
      title: 'Red pen',
      color: 'var(--ck-content-highlight-pen-red)',
      type: 'pen',
    },
  ],
};

const emoji : EmojiConfig = {
  dropdownLimit: 4,
  skinTone: 'default',
};

// Editor configuration.
const classicConfig: EditorConfig = {
  licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MDM2ODYzOTksImp0aSI6ImZhNTIyOWRjLWFiYTMtNDJlNC1iYzFlLTRiYjU1NDRhNjg3NyIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCJdLCJ2YyI6ImNkNjM3ZTBlIn0.giOq2hdiGkKd5SyFwDMTLxP7ilRPyD01iHgtfDnPvfEr6puSJZHP1jnOpp4CLdlUynBUqIS2JCQWdIGM2IcSwg',
  fontColor,
  fontFamily,
  heading,
  highlight,
  link,
  toolbar: {
    items: [
      'media',
      '|',
      'undo',
      'redo',
      '|',
      'bulbHeading',
      '|',
      'fontFamily',
      'fontColor',
      'highlight',
      'bold',
      'italic',
      'underline',
      'subscript',
      'superscript',
      'alignment',
      '|',
      'list',
      'outdent',
      'indent',
      'blockQuote',
      '|',
      'link',
      'bulbComment',
      'emoji',
    ],
  },

  list: {
    multiBlock: false,
    properties: {
      styles: {
        listTypes: ['bulleted', 'numbered'],
      },
    },
  },

  table: {
    tableProperties: {
      colorPicker: false,
      defaultProperties: {
        borderStyle: 'solid',
        borderWidth: '2',
      },
    },
    tableCellProperties: {
      colorPicker: false,
      defaultProperties: {
        borderStyle: 'solid',
        borderWidth: '2',
      },
    },
    contentToolbar: [
      'tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties', 'toggleTableCaption',
    ],
  },

  // This value must be kept in sync with the language defined in webpack.config.js.
  language: 'en',
  plugins: EDITOR_PLUGINS,
};

const balloonConfig = {
  licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4MDM2ODYzOTksImp0aSI6ImZhNTIyOWRjLWFiYTMtNDJlNC1iYzFlLTRiYjU1NDRhNjg3NyIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCJdLCJ2YyI6ImNkNjM3ZTBlIn0.giOq2hdiGkKd5SyFwDMTLxP7ilRPyD01iHgtfDnPvfEr6puSJZHP1jnOpp4CLdlUynBUqIS2JCQWdIGM2IcSwg',
  fontColor,
  fontFamily,
  heading,
  link,
  plugins: EDITOR_PLUGINS,
};

export default {
  classicConfig, balloonConfig,
};
